import type { ProjectFile } from '../../worker/shared/contracts';
import type { OpenDocument } from './documentStore';

/** Short-lived, single-consumption read-ahead. Never an offline authority or a write cache. */
export class FilePrefetch {
  private entries = new Map<string, { started: number; result: Promise<OpenDocument> }>();
  constructor(private load: (projectId: string, fileId: string, file?: ProjectFile) => Promise<OpenDocument>, private clock = Date.now) {}
  private key(projectId: string, fileId: string, file?: ProjectFile) {
    return `${projectId}/${fileId}/${file?.currentRevision ?? -1}`;
  }
  clear(projectId?: string, fileId?: string) {
    for (const key of this.entries.keys()) if (!projectId || key.startsWith(`${projectId}/${fileId ? fileId + '/' : ''}`)) this.entries.delete(key);
  }
  prefetch(projectId: string, file: ProjectFile) {
    // Avoid speculative model/image downloads and large embedded notebook assets.
    if (!['note', 'aml', 'system', 'code'].includes(file.kind) || !file.currentRevision || file.logicalSize > 512 * 1024) return;
    const key = this.key(projectId, file.id, file), entry = this.entries.get(key);
    if (entry && this.clock() - entry.started < 10000) return;
    this.entries.delete(key);
    while (this.entries.size >= 3) this.entries.delete(this.entries.keys().next().value!);
    const result = this.load(projectId, file.id, file);
    const next = { started: this.clock(), result };
    this.entries.set(key, next);
    void result.then(opened => {
      if (opened.bytes.byteLength > 512 * 1024 && this.entries.get(key) === next) this.entries.delete(key);
    }, () => { if (this.entries.get(key) === next) this.entries.delete(key); });
  }
  open(projectId: string, fileId: string, file?: ProjectFile) {
    const key = this.key(projectId, fileId, file), entry = this.entries.get(key);
    this.entries.delete(key); // Every later open must fetch again or start new read-ahead.
    return entry && this.clock() - entry.started < 10000 ? entry.result : this.load(projectId, fileId, file);
  }
}
